import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {Card, CardContent, CardHeader, Collapse, Grid, IconButton, LinearProgress, Stack, Typography} from '@mui/material';
import Button from '@mui/material/Button';
import {ArrowBack, ExpandLess, ExpandMore, PlaylistAddCheck} from '@mui/icons-material';
import {Formik, FormikErrors} from 'formik';
import {useAppDispatch} from '../store';
import {useAppTranslation} from '../services/i18n';
import {useIsKindOfAdmin, useMandatoryParams} from '../services/currentSpace';
import {isApiResultError} from '../helpers/api';
import {todayYmd} from '../helpers/date';
import {fromDateInput, isPriceValid, toDateInput, toPrice, toStudyTime, toStudyTimeMinutes} from 'model/approvalForm';
import {addMessage} from 'store/localApp';
import {createApprovalRequest, fetchApprovalRequest, updateApprovalRequest} from 'store/approvals';
import {ApprovalRequestDetail, isApprovalEditable, isLearningRequest} from 'model/approval';
import {OptionValue} from 'model/form';
import {TextFormField} from 'components/form/TextFormField';
import {SelectFormField} from 'components/form/SelectFormField';
import {CompetenceSelectFormField} from 'components/form/CompetenceSelectFormField';
import {ApprovalFileField, ApprovalFileValue} from 'components/approval/ApprovalFileField';
import {ApprovalUserSelectField} from 'components/approval/ApprovalUserSelectField';
import {ApprovalRequestForm} from 'components/approval/ApprovalRequestForm';
import {UserAttributeCell} from 'components/approval/ApprovalReadOnly';
import {ReadOnlyField} from 'components/ReadOnlyField';
import {approvalDetailUrl, backTargetFromParam} from 'pages/ApprovalDetailPage';
import type {ApprovalRequestUserEligibleUsersResponse} from '../generated-api';

const FORM_VALUES = ['online', 'combined', 'in_person', 'in_person_and_online'];

/** `#/approvals/new?type=request` opens the request form instead of the completion one */
export const REQUEST_TYPE_PARAM = 'request';

interface ApprovalFormValues {
    user_id?: number;
    subject_title: string;
    subject_date: string;
    subject_provider: string;
    subject_content: string;
    competence_ids: number[];
    study_time: string;
    form: string;
    budget: string;
    subject_expiration_date: string;
    price: string;
    note: string;
    certificate: ApprovalFileValue[];
    attachments: ApprovalFileValue[];
}

const emptyValues: ApprovalFormValues = {
    subject_title: '', subject_date: '', subject_provider: '', subject_content: '',
    competence_ids: [], study_time: '', form: '', budget: '', subject_expiration_date: '',
    price: '', note: '', certificate: [], attachments: []
};

/** `ApprovalRequest::MAX_FILES` -- the certificate is counted in as well. */
const MAX_FILES = 10;

/** `subject_title` length validation on the model. */
const MIN_TITLE_LENGTH = 3;

export const ApprovalFormPage = () => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const {approvalId} = useParams();
    const [searchParams] = useSearchParams();
    const mandatoryParams = useMandatoryParams();
    const isKindOfAdmin = useIsKindOfAdmin();

    const isEdit = !!approvalId;

    const [detail, setDetail] = useState<ApprovalRequestDetail>();
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSaving, setIsSaving] = useState(false);
    const [eligibleUsers, setEligibleUsers] = useState<ApprovalRequestUserEligibleUsersResponse[]>([]);
    // the optional fields are part of the job here, not an afterthought, so the
    // section opens with the form
    const [isDetailsOpen, setIsDetailsOpen] = useState(true);

    useEffect(() => {
        if (!approvalId) {
            return;
        }
        setIsLoading(true);
        dispatch(fetchApprovalRequest({id: approvalId}))
            .then((res) => setDetail(isApiResultError(res) ? undefined : (res.payload as ApprovalRequestDetail)))
            .finally(() => setIsLoading(false));
    }, [approvalId, dispatch]);

    const handleBack = useCallback(() => {
        window.location.href = isEdit
            ? approvalDetailUrl(approvalId, searchParams.get('from'))
            : backTargetFromParam(searchParams.get('from'));
    }, [isEdit, approvalId, searchParams]);

    const initialValues: ApprovalFormValues = useMemo(() => {
        if (!detail) {
            return emptyValues;
        }
        return {
            user_id: detail.user?.id,
            subject_title: detail.subject_title || '',
            subject_date: toDateInput(detail.subject_date),
            subject_provider: detail.subject_provider || '',
            subject_content: detail.subject_content || '',
            competence_ids: detail.competences?.map(c => c.id) || [],
            study_time: detail.study_time || '',
            form: detail.form || '',
            // both live on the certificate association, not on the record itself
            budget: detail.external_certificate?.budget || '',
            subject_expiration_date: toDateInput(detail.external_certificate?.expiration_date),
            price: (detail.price || detail.price === 0) ? String(detail.price) : '',
            note: detail.note || '',
            certificate: detail.certificate
                ? [{
                    id: detail.certificate.id, file_name: detail.certificate.file_name,
                    size: detail.certificate.size, download_url: detail.certificate.download_url
                }]
                : [],
            attachments: detail.attachments?.map(f =>
                ({id: f.id, file_name: f.file_name, size: f.size, download_url: f.download_url})) || [],
        };
    }, [detail]);

    const handleValidate = useCallback((values: ApprovalFormValues) => {
        const errors: FormikErrors<ApprovalFormValues> = {};
        if (!isEdit && !values.user_id) {
            errors.user_id = t('form.required');
        }
        if (!values.subject_title?.trim()) {
            errors.subject_title = t('form.required');
        } else if (values.subject_title.trim().length < MIN_TITLE_LENGTH) {
            errors.subject_title = t('approvals.form.subjectTitleTooShort', {min: MIN_TITLE_LENGTH});
        }
        if (!values.subject_date) {
            errors.subject_date = t('form.required');
        } else if (values.subject_date > todayYmd()) {
            // a completion records a course that already happened -- the API refuses
            // a future term with subject_date :in_future
            errors.subject_date = t('approvals.form.subjectDateInFuture');
        }
        if (!values.certificate.length) {
            errors.certificate = t('form.required') as any;
        }
        // the certificate counts towards the same limit as the extra files
        if (values.certificate.length + values.attachments.length > MAX_FILES) {
            errors.attachments = t('approvals.file.tooMany', {max: MAX_FILES}) as any;
        }
        // the API refuses a certificate that expires before the course happened
        if (values.subject_expiration_date && values.subject_date
            && values.subject_expiration_date < values.subject_date) {
            errors.subject_expiration_date = t('approvals.form.expirationBeforeSubjectDate');
        }
        if (values.study_time.trim()) {
            const minutes = toStudyTimeMinutes(values.study_time);
            if (minutes === undefined) {
                errors.study_time = t('approvals.form.studyTimeInvalid');
            } else if (minutes <= 0) {
                // the API stores study_time in minutes and insists on a positive value
                errors.study_time = t('approvals.form.studyTimeNotPositive');
            }
        }
        if (values.price.trim() && !isPriceValid(values.price)) {
            errors.price = t('approvals.form.priceInvalid');
        }
        return errors;
    }, [isEdit, t]);

    const handleSubmit = useCallback(async (values: ApprovalFormValues) => {
        setIsSaving(true);
        const shared = {
            subject_title: values.subject_title.trim(),
            subject_provider: values.subject_provider || undefined,
            subject_content: values.subject_content || undefined,
            competence_ids: values.competence_ids,
            study_time: toStudyTime(values.study_time),
            form: (values.form || undefined) as any,
            budget: values.budget || undefined,
            subject_expiration_date: fromDateInput(values.subject_expiration_date),
            price: toPrice(values.price),
            note: values.note || undefined,
            attachment_file_ids: values.attachments.map(f => f.id),
        };

        let res;
        if (isEdit && detail) {
            const certificateId = values.certificate[0]?.id;
            const body = {
                ...shared,
                subject_date: fromDateInput(values.subject_date),
                // only send it when the file actually changed
                certificate_file_id: certificateId === detail.certificate?.id ? undefined : certificateId,
            };
            res = await dispatch(updateApprovalRequest({id: String(approvalId), body}));
        } else {
            res = await dispatch(createApprovalRequest({
                body: {
                    ...shared,
                    user_id: values.user_id!,
                    subject_date: fromDateInput(values.subject_date)!,
                    certificate_file_id: values.certificate[0].id,
                }
            }));
        }
        setIsSaving(false);

        if (isApiResultError(res)) {
            // apiErrorMiddleware already surfaces the server's own explanation
            dispatch(addMessage({
                severity: 'error',
                title: t(isEdit ? 'approvals.toast.updateFailed' : 'approvals.toast.createFailed')
            }));
            return;
        }
        dispatch(addMessage({severity: 'success', title: t(isEdit ? 'approvals.toast.updated' : 'approvals.toast.created')}));
        window.location.href = approvalDetailUrl(
            String((res.payload as ApprovalRequestDetail)?.id || approvalId), searchParams.get('from'));
    }, [isEdit, detail, approvalId, dispatch, searchParams, t]);

    const formOptions: OptionValue[] = useMemo(() =>
        FORM_VALUES.map(v => ({value: v, label: t('approvals.formValue.' + v)})), [t]);

    // the API refuses the save anyway, but a student who reaches the URL directly
    // should not get a form that looks editable
    if (!isKindOfAdmin) {
        return <div className={'kws-content-margin'}>
            <div className={'data-grid-zero-data'}>{t('approvals.form.noAccess')}</div>
        </div>;
    }

    if (isLoading) {
        return <LinearProgress/>;
    }

    if (isEdit && !detail) {
        return <div className={'kws-content-margin'}>
            <div className={'data-grid-zero-data'}>{t('approvals.notFound')}</div>
        </div>;
    }

    // a request is the other half of the agenda: filed for a student and then
    // worked up into a decidable record, rather than a completed course
    if (!isEdit && searchParams.get('type') === REQUEST_TYPE_PARAM) {
        return <ApprovalRequestForm onBack={handleBack}/>;
    }

    if (detail && isLearningRequest(detail)) {
        return isApprovalEditable(detail)
            ? <ApprovalRequestForm detail={detail} onBack={handleBack}/>
            : <div className={'kws-content-margin'}>
                <div className={'data-grid-zero-data'}>{t('approvals.form.notEditable')}</div>
            </div>;
    }

    return <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={handleValidate} enableReinitialize>
        {({handleSubmit: submit, values, errors, touched, submitCount, setFieldValue}) => {
            const selectedUser = eligibleUsers.find(u => u.id === values.user_id);
            const user = detail?.user || selectedUser;
            const certificateError = (touched.certificate || submitCount > 0) ? errors.certificate as string : undefined;
            const attachmentsError = (touched.attachments || submitCount > 0) ? errors.attachments as string : undefined;
            // an error hidden inside the collapsed section would block a save with
            // nothing on screen to explain it
            const detailsOpen = isDetailsOpen || (submitCount > 0
                && !!(errors.subject_expiration_date || errors.study_time || errors.price));

            // min/max on the date inputs are there to constrain the native picker;
            // without noValidate the browser would block the submit with its own
            // transient bubble and our inline messages would never be reached
            return <form onSubmit={submit} noValidate className={'layout-fill layout-column tw-absolute'}>
                <div className={'kws-content-margin flex tw-h-100'}>
                    <Card className={'tw-h-full tw-flex tw-flex-col'}>
                        <CardHeader
                            className={'max-sm:tw-flex-wrap max-sm:tw-gap-12'}
                            title={<>
                                <IconButton onClick={handleBack} color={'inherit'} aria-label={t('approvals.action.back')}>
                                    <ArrowBack/>
                                </IconButton>
                                <PlaylistAddCheck/>
                                <span>{isEdit ? t('approvals.form.editTitle') : t('approvals.form.createTitle')}</span>
                            </>}
                            action={<Button color={'primary'} variant={'contained'} type={'submit'}
                                disabled={isSaving} className={'tw-w-auto'}>{t('form.save')}</Button>}
                        />
                        <CardContent className={'tw-p-32 tw-flex-grow tw-flex tw-flex-col tw-overflow-auto'}>
                            <Grid container spacing={3}>
                                <Grid size={{xs: 12, md: 3}}>
                                    {isEdit
                                        // the record's user cannot be changed
                                        ? <ReadOnlyField underline label={t('approvals.field.fullName')} value={user?.full_name}/>
                                        : <ApprovalUserSelectField name={'user_id'}
                                            label={t('approvals.field.fullName')}
                                            placeholder={t('approvals.form.addUser')}
                                            onLoaded={setEligibleUsers}/>}
                                </Grid>
                                {/* only the attributes this space actually collects, like the AngularJS user screens */}
                                <UserAttributeCell show={mandatoryParams.personal_id}>
                                    <ReadOnlyField underline label={t('approvals.field.personalId')} value={user?.personal_id}/>
                                </UserAttributeCell>
                                <UserAttributeCell show={mandatoryParams.department}>
                                    <ReadOnlyField underline label={t('approvals.field.department')} value={user?.department}/>
                                </UserAttributeCell>
                                <UserAttributeCell show={mandatoryParams.job_title}>
                                    <ReadOnlyField underline label={t('approvals.field.jobTitle')} value={user?.job_title}/>
                                </UserAttributeCell>

                                <Grid size={{xs: 12, md: 6}}>
                                    <Stack spacing={3}>
                                        <TextFormField name={'subject_title'} label={t('approvals.field.subjectTitle')} required maxlength={255}/>
                                        <TextFormField name={'subject_date'} label={t('approvals.field.subjectDate')} type={'date'}
                                            required maxValue={todayYmd()}/>
                                    </Stack>
                                </Grid>
                                <Grid size={{xs: 12, md: 6}}>
                                    <Stack spacing={3}>
                                        <ApprovalFileField
                                            label={t('approvals.form.certificateLabel')}
                                            hint={t('approvals.file.types')}
                                            required
                                            value={values.certificate}
                                            error={certificateError}
                                            onChange={(files) => setFieldValue('certificate', files)}/>
                                        <ApprovalFileField
                                            label={t('approvals.field.attachments')}
                                            multiple
                                            maxFiles={MAX_FILES}
                                            usedFiles={values.certificate.length}
                                            value={values.attachments}
                                            error={attachmentsError}
                                            onChange={(files) => setFieldValue('attachments', files)}/>
                                        {/* the student opens the same detail, so nothing here is internal */}
                                        <Typography component={'div'} sx={{fontSize: '12px', color: 'var(--color-black-inactive)'}}>
                                            {t('approvals.form.filesVisibleToStudent', {max: MAX_FILES})}
                                        </Typography>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <div className={'tw-border tw-border-solid tw-rounded-md tw-p-24 tw-mt-32'}
                                style={{borderColor: 'var(--color-black-inactive)'}}>
                                <div role={'button'} tabIndex={0}
                                    className={'tw-flex tw-items-center tw-gap-8 tw-cursor-pointer'}
                                    aria-expanded={detailsOpen}
                                    onClick={() => setIsDetailsOpen(o => !o)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setIsDetailsOpen(o => !o);
                                        }
                                    }}>
                                    {detailsOpen ? <ExpandLess/> : <ExpandMore/>}
                                    <Typography component={'div'}>{t('approvals.field.details')}</Typography>
                                </div>
                                <Collapse in={detailsOpen} unmountOnExit={false}>
                                <Grid container spacing={3} className={'tw-pt-24'}>
                                    {/* laid out row by row so the three columns stay on one baseline
                                        even though the expiration hint makes its cell taller */}
                                    <Grid size={{xs: 12, md: 4}}>
                                        <TextFormField name={'subject_provider'} label={t('approvals.field.provider')} maxlength={255}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        <TextFormField name={'study_time'} label={t('approvals.field.studyTime')}
                                            placeholder={t('approvals.form.studyTimePlaceholder')}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        <TextFormField name={'subject_expiration_date'} label={t('approvals.field.expiration')} type={'date'}
                                            minValue={values.subject_date || undefined}/>
                                        <Typography component={'div'} className={'tw-mt-4'}
                                            sx={{fontSize: '12px', color: 'var(--color-black-inactive)'}}>
                                            {t('approvals.hint.expiration')}
                                        </Typography>
                                    </Grid>

                                    <Grid size={{xs: 12, md: 4}}>
                                        <TextFormField name={'subject_content'} label={t('approvals.field.content')} maxlength={255}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        {/* shrink keeps the label above the line, level with the text fields in the same row */}
                                        <SelectFormField name={'form'} options={formOptions} label={t('approvals.field.form')} shrink/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        <TextFormField name={'price'} label={t('approvals.field.price')}/>
                                    </Grid>

                                    <Grid size={{xs: 12, md: 4}}>
                                        <CompetenceSelectFormField name={'competence_ids'} isMulti
                                            label={t('approvals.field.competences')} shrink/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}>
                                        <TextFormField name={'budget'} label={t('approvals.field.budget')} maxlength={255}/>
                                    </Grid>
                                    <Grid size={{xs: 12, md: 4}}/>
                                    <Grid size={12}>
                                        <TextFormField name={'note'} label={t('approvals.field.note')} maxlength={1000}/>
                                        <Typography component={'div'} sx={{fontSize: '12px', color: 'var(--color-black-inactive)'}}>
                                            {t('approvals.hint.note')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                </Collapse>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </form>;
        }}
    </Formik>;
};
